import React, { useEffect, useMemo, useState } from "react";

import {
  ArrayUtils,
  getMidpointGrowingDate,
  normalizeDiacritics,
  useFieldGeometry,
  useGetCarbonProgramByFarmId,
  useGetFarmFieldListAtPointInTime,
  useGetFieldDetails,
  useGetFields,
  useGetFootprintingCropAssessmentsRequiringReview,
  useGetFootprintingCropFootprints,
  useGetFootprintingCropPrimaryDataById,
  useGetPracticeCrops,
  useMutateFootprintingCropPrimaryData,
  useTranslation,
} from "utils";

import { FieldListField } from "models";

import {
  AlertBanner,
  Button,
  Card,
  Checkbox,
  CheckboxChangeEvent,
  Drawer,
  ErrorBoundary,
  Flex,
  Form,
  Icon,
  InputSearch,
  Link,
  MapStatic,
  PageSpin,
  Spin,
  Tag,
  Typography,
} from "components";

import { FieldUpload } from "pages/app/fields/components/FieldUpload.tsx";

interface SelectableFieldRowProps {
  onChange: (e: Partial<CheckboxChangeEvent>) => void;
  field: FieldListField;
  checked: boolean;
  disabled?: boolean;
  dataTestId?: string;
  previouslySelectedCrop?: string;
  needsReview?: boolean;
}

const SelectableFieldRow = ({ disabled, onChange, field, checked, dataTestId, previouslySelectedCrop, needsReview }: SelectableFieldRowProps) => {
  const { t } = useTranslation("footprintingCrop");
  const { geometry: fieldGeometry, geometryLoading } = useFieldGeometry(field.id);
  const { data: fieldDetails, isLoading: fieldDetailsLoading } = useGetFieldDetails({ id: field.id });

  if (geometryLoading || fieldDetailsLoading) {
    return (
      <Flex className="padding-100 margin-100" align="center">
        <Spin size="small" />
        <Typography variant="paragraph-m" className="margin-left-50">
          {t("cropFieldSelection.loading")}
        </Typography>
      </Flex>
    );
  }

  const isPreviouslySelected = !!previouslySelectedCrop;

  return (
    <Flex
      data-testid={dataTestId}
      className={`padding-25 field-card ${checked ? "field-selected" : ""} ${
        isPreviouslySelected ? "field-previously-selected" : needsReview ? "field-needs-review" : ""
      }`}
      justify="space-between"
      onClick={() => !isPreviouslySelected && onChange({ target: { checked: !checked } })}
    >
      <Flex align="center" className="padding-100">
        <div className="map-container margin-right-100 field-card-map">
          {fieldGeometry && (
            <ErrorBoundary>
              {/* @ts-expect-error need to convert to tsx */}
              <MapStatic field={field} geometry={fieldGeometry} location={field?.location} />
            </ErrorBoundary>
          )}
        </div>
        <Flex vertical gap="0.5rem" className="field-info">
          {previouslySelectedCrop && (
            <Tag text={`${t("cropFieldSelection.usedFor")} ${previouslySelectedCrop}`} colour="yellow" className="margin-bottom-50 tag-wrapper" />
          )}
          <Flex gap="0.5rem">{fieldDetails?.number}</Flex>
          <Typography variant="h4">{field.name}</Typography>
          <Flex gap="0.5rem">
            <Icon iconName="klim/field-size" />
            <Typography variant="h6">{(field?.size ?? 0).toFixed(4)}</Typography>
          </Flex>
        </Flex>
      </Flex>
      <Checkbox disabled={disabled || isPreviouslySelected} onChange={onChange} checked={checked} />
    </Flex>
  );
};

const NoFieldsAvailable = () => {
  const { t } = useTranslation("footprintingCrop");
  const [showFieldUploadDrawer, setShowFieldUploadDrawer] = useState(false);
  return (
    <>
      <Flex className="padding-100 margin-100 add-fields" align="center">
        <Flex className="padding-50">
          <Icon size="l" iconName="klim/field" />
        </Flex>
        <Flex vertical justify="center" className="margin-left-50">
          <Typography variant="h5">{t("cropFieldSelection.noFieldsAvailable")}</Typography>
          <Typography variant="paragraph-m">{t("cropFieldSelection.assignFields")}</Typography>
        </Flex>
        <Flex vertical justify="center" align="center" className="padding-left-100 padding-right-50">
          <Button size="small" type="primary" className="button-no-min-width" onClick={() => setShowFieldUploadDrawer(true)}>
            {t("cropFieldSelection.addField")}
          </Button>
        </Flex>
      </Flex>
      <Drawer
        className="add-fields-drawer-footprinting-form"
        visible={showFieldUploadDrawer}
        title={t("cropFieldSelection.uploadFieldDrawer")}
        onClose={() => setShowFieldUploadDrawer(false)}
      >
        <FieldUpload onFinish={() => setShowFieldUploadDrawer(false)} />
      </Drawer>
    </>
  );
};

const NoFieldsFound = () => {
  const { t } = useTranslation("footprintingCrop");

  return (
    <Flex className="padding-100 margin-100 add-fields" align="center">
      <Flex className="padding-50">
        <Icon size="l" iconName="klim/field" />
      </Flex>
      <Flex vertical justify="center" className="margin-left-100">
        <Typography variant="h5">{t("cropFieldSelection.noFieldsFound")}</Typography>
        <Typography variant="paragraph-m">{t("cropFieldSelection.changeSearch")}</Typography>
      </Flex>
    </Flex>
  );
};

type CropFieldSelectionFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
};

const CropFieldSelectionForm = ({ readOnly, primaryDataId, farmId, cropId, year }: CropFieldSelectionFormProps) => {
  const { t } = useTranslation("footprintingCrop");
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);

  const cropFootprintsQueries = useGetFootprintingCropFootprints(
    farmId,
    [year],
    carbonProgram?.farmLevelFootprinting?.programCrops.filter((cid) => cid !== cropId),
    {
      enabled: true,
    }
  );

  const { data: allFields, isLoading: isAllFieldsLoading, isFetching: isAllFieldsFetching } = useGetFields({ farmId });
  const regionId = allFields?.find((f) => f.regionId)?.regionId;
  const { data: practiceCrops, isLoading: isPracticeCropsLoading } = useGetPracticeCrops({ regionId, enabled: !!regionId && !!allFields });
  const midpointDateForYear = getMidpointGrowingDate(practiceCrops?.find((c) => c.id === cropId), year);

  const {
    data: fieldListAtPointInTime,
    isLoading: isFieldListAtPointInTimeLoading,
    isFetching: isFieldListAtPointInTimeFetching,
  } = useGetFarmFieldListAtPointInTime({
    farmId,
    date: midpointDateForYear?.toISOString() ?? "",
    enabled: !!midpointDateForYear,
  });

  const { data: assessmentsRequiringReview, isLoading: isAssessmentsRequiringReviewLoading } = useGetFootprintingCropAssessmentsRequiringReview({
    enabled: true,
  });

  const assessmentRequiringReview = assessmentsRequiringReview?.[year]?.[cropId];

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  const isLoading = isPracticeCropsLoading || isFieldListAtPointInTimeLoading || isAllFieldsLoading || isAssessmentsRequiringReviewLoading;

  const isFetching = isFieldListAtPointInTimeFetching || isAllFieldsFetching;

  const [reviewFieldIds, setReviewFieldIds] = useState<string[]>(cropPrimaryDataQuery.data?.reviewFieldIds ?? []);

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      fieldIds: selectedFieldIds ?? [],
      reviewFieldIds,
    },
    primaryDataId
  );

  const finalFieldList = fieldListAtPointInTime && fieldListAtPointInTime?.length > 0 ? fieldListAtPointInTime : allFields;

  const fieldIdToFieldData: {
    [fieldId: string]: FieldListField | undefined;
  } = ArrayUtils.indexBy(finalFieldList ?? [], (field) => field.id);

  // Create mapping of field IDs to crop names for previously selected fields
  const fieldIdToOtherCropName = useMemo(
    () =>
      cropFootprintsQueries
        .flatMap((cfq) => cfq.data?.footprint?.fieldIds.map((fieldId) => ({ fieldId, cropId: cfq.data.cropId })) ?? [])
        .reduce(
          (acc, item) => {
            acc[item.fieldId] = practiceCrops?.find((crop) => crop.id === item.cropId)?.name;
            return acc;
          },
          {} as Record<string, string | undefined>
        ),
    [cropFootprintsQueries, practiceCrops]
  );

  const handleSelectAll = () => {
    if (finalFieldList) {
      if (selectedFieldIds.length > 0) {
        setSelectedFieldIds([]);
      } else {
        setSelectedFieldIds(finalFieldList?.filter((field) => fieldIdToOtherCropName[field.id] === undefined).map((field) => field.id) ?? []);
      }
    }
  };

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCropPrimaryData();
      }
    },
    [isLoading, mutateCropPrimaryData, readOnly]
  );

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (cropPrimaryDataQuery.data?.fieldIds) {
      const fieldIds = [...cropPrimaryDataQuery.data.fieldIds].sort();
      setSelectedFieldIds(fieldIds);
    }
  }, [cropPrimaryDataQuery.data?.fieldIds]);

  const queryVariants = useMemo(() => normalizeDiacritics(searchQuery), [searchQuery]);
  const filteredFields = useMemo(() => {
    const [selectableFields, disabledFields] = ArrayUtils.partition(
      (
        finalFieldList?.filter((field) => {
          if (searchQuery.trim() === "") return true;
          const nameVariants = normalizeDiacritics(field.name);
          return queryVariants.some((queryVariant: string) =>
            nameVariants.some((nameVariant: string) => nameVariant.toLowerCase().includes(queryVariant.toLowerCase()))
          );
        }) ?? []
      )
        .sort((a, b) => a.name.localeCompare(b.name))
        // Show fields that were split and therefore need to be reviewed first
        .sort((a, b) => (!cropPrimaryDataQuery.data?.reviewFieldIds?.includes(a.id) ? 1 : !cropPrimaryDataQuery.data?.reviewFieldIds?.includes(b.id) ? -1 : 0)),
      (f) => fieldIdToOtherCropName[f.id] === undefined
    );
    return [...selectableFields, ...disabledFields];
  }, [fieldIdToOtherCropName, finalFieldList, queryVariants, searchQuery, cropPrimaryDataQuery.data?.reviewFieldIds]);

  if (primaryDataId && (isLoading || isFetching)) return <PageSpin />;

  const selectedFieldHectares = Number(selectedFieldIds.reduce((totalHa, fieldId) => totalHa + (fieldIdToFieldData[fieldId]?.size ?? 0), 0)).toFixed(2);

  const handleDismissSplitBanner = () => {
    setReviewFieldIds([]);
    mutateCropPrimaryData();
  };

  return (
    <Card border dataTestId="footprinting-crop-fields-form" className="padding-200">
      <Flex vertical gap={16}>
        <Flex vertical align="center">
          {!assessmentRequiringReview ? (
            <AlertBanner
              type="info-higlighted"
              iconName="tabler/info-circle"
              iconSize={24}
              iconAlign="start"
              className="footprinting-alert-banner margin-bottom-200"
              description={t("cropFieldSelection.alertBanner")}
            />
          ) : null}
          <InputSearch style={{ width: "100%" }} label={t("search")} onChange={(e) => setSearchQuery(e.target.value)} value={searchQuery} />
          {assessmentRequiringReview && !assessmentRequiringReview.isSubmitted ? (
            <AlertBanner
              type="warning"
              className="margin-top-200"
              iconName="tabler/info-circle"
              iconSize={24}
              iconAlign="start"
              description={
                <Flex vertical gap={4}>
                  <Typography variant="h6">{t("assessmentManagement.unsubmittedAssessmentsAffectedBySplitBanner.header")}</Typography>
                  <Typography variant="paragraph-m">{t("assessmentManagement.unsubmittedAssessmentsAffectedBySplitBanner.description")}</Typography>
                  <Typography variant="paragraph-m" bold className="review-crop-assessments-button" onClick={handleDismissSplitBanner}>
                    {t("assessmentManagement.unsubmittedAssessmentsAffectedBySplitBanner.dismissButton")}
                  </Typography>
                </Flex>
              }
            />
          ) : null}
        </Flex>
        {/* Show "no fields available" only when there are no fields at all */}
        {finalFieldList && finalFieldList.length === 0 && <NoFieldsAvailable />}

        {/* Show "no fields found" only when there's a search query but no results */}
        {searchQuery.trim() !== "" && filteredFields.length === 0 && finalFieldList && finalFieldList.length > 0 && <NoFieldsFound />}

        {filteredFields.length > 0 && (
          <>
            <Flex justify="space-between" align="flex-start">
              <Link onClick={handleSelectAll} dataTestId="review-redzones-select-all-button" className="green-400-text">
                {selectedFieldIds.length > 0 ? t("cropFieldSelection.deselectAll") : t("cropFieldSelection.selectAll")}
              </Link>
              <Card
                border
                key={`summary-${selectedFieldIds.join(",")}`}
                className={`padding-25 padding-left-100 padding-right-100 live-summary-card ${selectedFieldIds.length > 0 ? "selected" : "empty"}`}
              >
                <Typography variant="paragraph-s">
                  {selectedFieldIds.length} {t("cropFieldSelection.selected")} – {selectedFieldHectares} ha {t("cropFieldSelection.total")}
                </Typography>
              </Card>
            </Flex>
            <Form>
              <Flex vertical gap={16}>
                {filteredFields.map((field) => (
                  <SelectableFieldRow
                    disabled={readOnly}
                    dataTestId="footprinting-crop-fields-form-list-select"
                    key={field.id}
                    field={field}
                    checked={selectedFieldIds.includes(field.id)}
                    previouslySelectedCrop={fieldIdToOtherCropName[field.id]}
                    needsReview={!assessmentRequiringReview?.isSubmitted && cropPrimaryDataQuery.data?.reviewFieldIds?.includes(field.id)}
                    onChange={(e) => {
                      if (e.target?.checked) {
                        setSelectedFieldIds([...selectedFieldIds, field.id]);
                      } else {
                        setSelectedFieldIds(selectedFieldIds.filter((id) => id !== field.id));
                      }
                    }}
                  />
                ))}
              </Flex>
            </Form>
          </>
        )}
      </Flex>
    </Card>
  );
};

CropFieldSelectionForm.Key = "crop.fields";
export { CropFieldSelectionForm };
